import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "requestsContainer",
    "requestRow",
    "searchInput",
    "filterButton",
    "preserveLogCheckbox",
    "disableCacheCheckbox",
    "detailsPanel",
    "detailsContent"
  ]

  static values = {
    buildSessionId: String,
    currentFilter: { type: String, default: "all" },
    searchTerm: { type: String, default: "" },
    preserveLog: { type: Boolean, default: false },
    disableCache: { type: Boolean, default: false },
    requests: { type: Array, default: [] },
    selectedRequestId: String
  }

  connect() {
    this.setupEventListeners()
    this.loadStoredRequests()
  }

  disconnect() {
    this.teardownEventListeners()
  }

  setupEventListeners() {
    this.element.addEventListener('preview-devtools:network-message', this.handleNetworkMessage.bind(this))
    this.element.addEventListener('preview-devtools:clear-network', this.clearRequests.bind(this))
    this.element.addEventListener('preview-devtools:export-network', this.exportRequests.bind(this))
  }

  teardownEventListeners() {
    this.element.removeEventListener('preview-devtools:network-message', this.handleNetworkMessage.bind(this))
    this.element.removeEventListener('preview-devtools:clear-network', this.clearRequests.bind(this))
    this.element.removeEventListener('preview-devtools:export-network', this.exportRequests.bind(this))
  }

  handleNetworkMessage(event) {
    const requestData = event.detail
    this.addRequest(requestData)
  }

  addRequest(requestData) {
    // Generate ID if not present
    requestData.id = requestData.id || `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    
    // Add to requests array
    this.requestsValue = [...this.requestsValue, requestData]
    
    // Create request row
    const row = this.createRequestRow(requestData, this.requestsValue.length - 1)
    
    // Remove empty state if present
    const emptyState = this.requestsContainerTarget.querySelector('tr td[colspan="8"]')
    if (emptyState) {
      emptyState.closest('tr').remove()
    }
    
    // Add to container
    this.requestsContainerTarget.appendChild(row)
    
    // Store if preserve log is enabled
    if (this.preserveLogValue) {
      this.storeRequest(requestData)
    }
  }

  createRequestRow(request, index) {
    const tr = document.createElement('tr')
    tr.className = 'hover:bg-base-200/50 cursor-pointer'
    tr.dataset.requestId = request.id
    tr.dataset.networkMonitorTarget = 'requestRow'
    tr.addEventListener('click', () => this.selectRequest({ currentTarget: tr }))
    
    // Index
    const tdIndex = document.createElement('td')
    tdIndex.textContent = index + 1
    tr.appendChild(tdIndex)
    
    // Name
    const tdName = document.createElement('td')
    tdName.className = 'truncate max-w-xs'
    tdName.title = request.url
    tdName.textContent = this.getRequestName(request)
    tr.appendChild(tdName)
    
    // Method
    const tdMethod = document.createElement('td')
    const methodBadge = document.createElement('span')
    methodBadge.className = `badge badge-xs ${this.getMethodBadgeClass(request.method)}`
    methodBadge.textContent = request.method || 'GET'
    tdMethod.appendChild(methodBadge)
    tr.appendChild(tdMethod)
    
    // Status
    const tdStatus = document.createElement('td')
    tdStatus.className = this.getStatusClass(request.status)
    tdStatus.textContent = request.status || '-'
    tr.appendChild(tdStatus)
    
    // Type
    const tdType = document.createElement('td')
    tdType.className = 'text-xs'
    tdType.textContent = this.getRequestType(request)
    tr.appendChild(tdType)
    
    // Size
    const tdSize = document.createElement('td')
    tdSize.className = 'text-xs'
    tdSize.textContent = this.formatSize(request.size)
    tr.appendChild(tdSize)
    
    // Time
    const tdTime = document.createElement('td')
    tdTime.className = 'text-xs'
    tdTime.textContent = this.formatDuration(request.duration)
    tr.appendChild(tdTime)
    
    // Waterfall
    const tdWaterfall = document.createElement('td')
    const waterfallBar = document.createElement('div')
    waterfallBar.className = 'w-full bg-base-300 rounded h-3'
    const waterfallProgress = document.createElement('div')
    waterfallProgress.className = 'bg-primary h-full rounded'
    waterfallProgress.style.width = `${Math.min((request.duration || 0) / 10, 100)}%`
    waterfallBar.appendChild(waterfallProgress)
    tdWaterfall.appendChild(waterfallBar)
    tr.appendChild(tdWaterfall)
    
    return tr
  }

  getRequestName(request) {
    if (!request.url) return '-'
    try {
      const url = new URL(request.url)
      return url.pathname.split('/').pop() || url.pathname || '/'
    } catch {
      return request.url.split('/').pop() || request.url
    }
  }

  getRequestType(request) {
    if (request.type) return request.type
    
    // Infer from URL or content-type
    const url = request.url?.toLowerCase() || ''
    const contentType = request.contentType?.toLowerCase() || ''
    
    if (url.endsWith('.js') || contentType.includes('javascript')) return 'JS'
    if (url.endsWith('.css') || contentType.includes('css')) return 'CSS'
    if (url.match(/\.(png|jpg|jpeg|gif|svg|webp)/i) || contentType.includes('image')) return 'Img'
    if (url.match(/\.(woff|woff2|ttf|eot)/i) || contentType.includes('font')) return 'Font'
    if (contentType.includes('json')) return 'XHR'
    if (contentType.includes('html')) return 'Doc'
    
    return 'XHR'
  }

  getMethodBadgeClass(method) {
    switch (method?.toUpperCase()) {
      case 'GET':
        return 'badge-info'
      case 'POST':
        return 'badge-success'
      case 'PUT':
      case 'PATCH':
        return 'badge-warning'
      case 'DELETE':
        return 'badge-error'
      default:
        return 'badge-ghost'
    }
  }

  getStatusClass(status) {
    const code = parseInt(status)
    if (code >= 200 && code < 300) return 'text-success'
    if (code >= 300 && code < 400) return 'text-info'
    if (code >= 400 && code < 500) return 'text-warning'
    if (code >= 500) return 'text-error'
    return 'text-base-content'
  }

  formatSize(bytes) {
    if (!bytes || bytes === 0) return '-'
    
    const units = ['B', 'KB', 'MB', 'GB']
    let size = bytes
    let unitIndex = 0
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024
      unitIndex++
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`
  }

  formatDuration(ms) {
    if (!ms || ms === 0) return '-'
    
    if (ms < 1000) {
      return `${Math.round(ms)}ms`
    } else {
      return `${(ms / 1000).toFixed(2)}s`
    }
  }

  selectRequest(event) {
    const requestId = event.currentTarget.dataset.requestId
    const request = this.requestsValue.find(r => r.id === requestId)
    
    if (!request) return
    
    // Update selected state
    this.requestRowTargets.forEach(row => {
      row.classList.toggle('bg-primary/10', row.dataset.requestId === requestId)
    })
    
    this.selectedRequestIdValue = requestId
    this.showRequestDetails(request)
  }

  showRequestDetails(request) {
    this.detailsPanelTarget.classList.remove('hidden')
    
    // Build details HTML
    const detailsHTML = `
      <div class="tabs-content">
        <!-- Headers Tab -->
        <div data-tab-panel="headers" class="space-y-2">
          <div>
            <h4 class="text-xs font-semibold mb-1">General</h4>
            <div class="text-xs space-y-1">
              <div><span class="text-base-content/60">URL:</span> ${request.url}</div>
              <div><span class="text-base-content/60">Method:</span> ${request.method}</div>
              <div><span class="text-base-content/60">Status:</span> ${request.status}</div>
            </div>
          </div>
          
          ${request.requestHeaders ? `
            <div>
              <h4 class="text-xs font-semibold mb-1">Request Headers</h4>
              <div class="text-xs space-y-1">
                ${Object.entries(request.requestHeaders).map(([key, value]) => 
                  `<div><span class="text-base-content/60">${key}:</span> ${value}</div>`
                ).join('')}
              </div>
            </div>
          ` : ''}
          
          ${request.responseHeaders ? `
            <div>
              <h4 class="text-xs font-semibold mb-1">Response Headers</h4>
              <div class="text-xs space-y-1">
                ${Object.entries(request.responseHeaders).map(([key, value]) => 
                  `<div><span class="text-base-content/60">${key}:</span> ${value}</div>`
                ).join('')}
              </div>
            </div>
          ` : ''}
        </div>
        
        <!-- Response Tab -->
        <div data-tab-panel="response" class="hidden">
          <pre class="text-xs overflow-auto">${request.responseBody || 'No response body'}</pre>
        </div>
        
        <!-- Timing Tab -->
        <div data-tab-panel="timing" class="hidden">
          <div class="text-xs space-y-1">
            <div><span class="text-base-content/60">Duration:</span> ${this.formatDuration(request.duration)}</div>
            ${request.timing ? Object.entries(request.timing).map(([key, value]) => 
              `<div><span class="text-base-content/60">${key}:</span> ${this.formatDuration(value)}</div>`
            ).join('') : ''}
          </div>
        </div>
      </div>
    `
    
    this.detailsContentTarget.innerHTML = detailsHTML
    
    // Setup tab switching
    const tabs = this.detailsPanelTarget.querySelectorAll('.tab')
    tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        const tabName = e.currentTarget.dataset.tab
        
        // Update tab states
        tabs.forEach(t => t.classList.toggle('tab-active', t === e.currentTarget))
        
        // Show/hide panels
        this.detailsContentTarget.querySelectorAll('[data-tab-panel]').forEach(panel => {
          panel.classList.toggle('hidden', panel.dataset.tabPanel !== tabName)
        })
      })
    })
  }

  applyFilter(event) {
    const filter = event.currentTarget.dataset.filter
    this.currentFilterValue = filter
    
    // Update button states
    this.filterButtonTargets.forEach(btn => {
      if (btn.dataset.filter === filter) {
        btn.classList.add('btn-active')
      } else {
        btn.classList.remove('btn-active')
      }
    })
    
    this.filterRequests()
  }

  filterRequests() {
    const searchTerm = this.searchInputTarget.value.toLowerCase()
    this.searchTermValue = searchTerm
    
    this.requestRowTargets.forEach((row, index) => {
      const request = this.requestsValue[index]
      if (!request) return
      
      const type = this.getRequestType(request).toLowerCase()
      const url = request.url?.toLowerCase() || ''
      
      // Check filter
      let matchesFilter = this.currentFilterValue === 'all' || type === this.currentFilterValue
      
      // Check search
      let matchesSearch = !searchTerm || url.includes(searchTerm)
      
      // Show/hide row
      row.classList.toggle('hidden', !(matchesFilter && matchesSearch))
    })
  }

  clearRequests() {
    this.requestsValue = []
    this.requestsContainerTarget.innerHTML = `
      <tr>
        <td colspan="8" class="text-center py-8 text-base-content/40">
          <i class="fas fa-globe text-2xl mb-2"></i>
          <p>Network activity will appear here</p>
        </td>
      </tr>
    `
    this.detailsPanelTarget.classList.add('hidden')
    this.clearStoredRequests()
  }

  togglePreserveLog(event) {
    this.preserveLogValue = event.target.checked
    if (!this.preserveLogValue) {
      this.clearStoredRequests()
    }
  }

  toggleDisableCache(event) {
    this.disableCacheValue = event.target.checked
    // Send message to iframe to disable cache
    const iframe = document.querySelector('#preview-iframe')
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage({
        type: 'disable-cache',
        value: this.disableCacheValue
      }, '*')
    }
  }

  exportRequests() {
    const data = {
      requests: this.requestsValue,
      exportedAt: new Date().toISOString(),
      filter: this.currentFilterValue,
      searchTerm: this.searchTermValue
    }
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `network-requests-${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // Storage methods
  storeRequest(requestData) {
    try {
      const key = `network-requests-${this.buildSessionIdValue}`
      const stored = localStorage.getItem(key)
      const requests = stored ? JSON.parse(stored) : []
      requests.push(requestData)
      // Keep only last 500 requests
      if (requests.length > 500) {
        requests.splice(0, requests.length - 500)
      }
      localStorage.setItem(key, JSON.stringify(requests))
    } catch (e) {
      console.error('Failed to store request:', e)
    }
  }

  loadStoredRequests() {
    if (!this.preserveLogValue) return
    
    try {
      const key = `network-requests-${this.buildSessionIdValue}`
      const stored = localStorage.getItem(key)
      if (stored) {
        const requests = JSON.parse(stored)
        requests.forEach(request => this.addRequest(request))
      }
    } catch (e) {
      console.error('Failed to load stored requests:', e)
    }
  }

  clearStoredRequests() {
    try {
      const key = `network-requests-${this.buildSessionIdValue}`
      localStorage.removeItem(key)
    } catch (e) {
      console.error('Failed to clear stored requests:', e)
    }
  }
}